import React, { useState } from "react";
import {
  reprocessRepo,
  GitHubProxyError,
  type ReprocessResponse,
} from "../lib/github-proxy-client";
import MaintainerCoverageSection from "../components/MaintainerCoverageSection";

interface Props {
  onBack: () => void;
}

export default function AdminPage({ onBack }: Props) {
  const [source, setSource] = useState<"ghec" | "ghes">("ghes");
  const [org, setOrg] = useState("");
  const [repo, setRepo] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ReprocessResponse | null>(null);
  const [error, setError] = useState("");

  const canSubmit = org.trim() !== "" && repo.trim() !== "" && !loading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setError("");

    try {
      const res = await reprocessRepo({
        source,
        org: org.trim(),
        repo: repo.trim(),
      });
      setResult(res);
    } catch (err) {
      if (err instanceof GitHubProxyError) {
        setError(
          err.statusCode === 403
            ? "Access denied — your account is not an admin."
            : err.message,
        );
      } else {
        setError(err instanceof Error ? err.message : "Unknown error");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <button
        onClick={onBack}
        style={{
          background: "none",
          border: "none",
          color: "var(--color-brand)",
          cursor: "pointer",
          fontSize: "1rem",
          padding: 0,
          marginBottom: "1.5rem",
        }}
      >
        &larr; Back to Marketplace
      </button>

      <h2 style={{ marginTop: 0 }}>Admin: Reprocess Repo</h2>
      <p style={{ color: "var(--color-text-secondary)", marginBottom: "1.5rem" }}>
        Crawl a GitHub repo for all <code>.claude-plugin/plugin.json</code> manifests
        and re-run the processor pipeline for each discovered skill.
      </p>

      <form onSubmit={handleSubmit} style={{ maxWidth: 480 }}>
        <div style={{ marginBottom: "1rem" }}>
          <label htmlFor="admin-source" style={{ display: "block", fontWeight: 600, marginBottom: "0.25rem" }}>
            Source
          </label>
          <select
            id="admin-source"
            value={source}
            onChange={(e) => setSource(e.target.value as "ghec" | "ghes")}
            style={{
              padding: "0.5rem",
              fontSize: "1rem",
              border: "1px solid var(--color-border)",
              borderRadius: "4px",
              width: "100%",
            }}
          >
            <option value="ghec">GHEC (github.com)</option>
            <option value="ghes">GHES (ghe.coxautoinc.com)</option>
          </select>
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label htmlFor="admin-org" style={{ display: "block", fontWeight: 600, marginBottom: "0.25rem" }}>
            Org / Owner
          </label>
          <input
            id="admin-org"
            type="text"
            value={org}
            onChange={(e) => setOrg(e.target.value)}
            placeholder="e.g. CoxAuto"
            style={{
              padding: "0.5rem",
              fontSize: "1rem",
              border: "1px solid var(--color-border)",
              borderRadius: "4px",
              width: "100%",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label htmlFor="admin-repo" style={{ display: "block", fontWeight: 600, marginBottom: "0.25rem" }}>
            Repository
          </label>
          <input
            id="admin-repo"
            type="text"
            value={repo}
            onChange={(e) => setRepo(e.target.value)}
            placeholder="e.g. contributed-skills"
            style={{
              padding: "0.5rem",
              fontSize: "1rem",
              border: "1px solid var(--color-border)",
              borderRadius: "4px",
              width: "100%",
              boxSizing: "border-box",
            }}
          />
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            padding: "0.6rem 1.5rem",
            fontSize: "1rem",
            backgroundColor: canSubmit ? "var(--color-brand-emphasis)" : "var(--color-border)",
            color: "var(--color-on-brand)",
            border: "none",
            borderRadius: "4px",
            cursor: canSubmit ? "pointer" : "not-allowed",
            fontWeight: 600,
          }}
        >
          {loading ? "Processing..." : "Reprocess"}
        </button>
      </form>

      {error && (
        <div
          style={{
            marginTop: "1.5rem",
            padding: "1rem",
            backgroundColor: "var(--color-error-bg)",
            border: "1px solid var(--color-error-border)",
            borderRadius: "4px",
            color: "var(--color-error)",
          }}
        >
          {error}
        </div>
      )}

      {result && (
        <div
          style={{
            marginTop: "1.5rem",
            padding: "1rem",
            backgroundColor: "var(--color-success-bg)",
            border: "1px solid var(--color-success-border)",
            borderRadius: "4px",
            color: "var(--color-success)",
          }}
        >
          <strong>Enqueued for reprocessing</strong>
          <div style={{ marginTop: "0.5rem", fontSize: "0.9rem" }}>
            <div>
              <strong>Repo:</strong> {result.source}:{result.org}/{result.repo}
            </div>
            <div>
              <strong>Branch:</strong> {result.defaultBranch}
            </div>
            <div>
              <strong>Commit:</strong> {result.commitSha}
            </div>
            <div>
              <strong>Manifests found:</strong> {result.manifestPaths.length}
              {result.truncated && " (tree truncated — large repo)"}
            </div>
            <ul style={{ margin: "0.5rem 0 0", paddingLeft: "1.5rem" }}>
              {result.manifestPaths.map((p) => (
                <li key={p} style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <MaintainerCoverageSection />
    </div>
  );
}
